import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import useStore from "../../../store/useStore";
import Loader from "../../../components/Loader";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";

const getProfileValue = (user, key) =>
  user?.[key] ?? user?.customer?.[key] ?? "";

const buildForm = (user) => ({
  name: getProfileValue(user, "name") || getProfileValue(user, "full_name"),
  email: getProfileValue(user, "email"),
  mobile: getProfileValue(user, "mobile")
    ? String(getProfileValue(user, "mobile"))
    : "",
});

const Field = ({ label, value, editing, name, onChange, disabled = false }) => (
  <div>
    <label className="mb-1.5 block text-sm font-medium text-gray-700">
      {label}
    </label>
    {editing && !disabled ? (
      <input
        name={name}
        value={value}
        onChange={onChange}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
      />
    ) : (
      <div className="min-h-[40px] rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900">
        {value || <span className="text-gray-400">Not set</span>}
      </div>
    )}
    {disabled && <p className="mt-1 text-xs text-gray-500">Verified detail</p>}
  </div>
);

const ProfileSection = () => {
  const {
    user,
    isAuthenticated,
    profile_loading,
    profile_error,
    fetchProfile,
    updateProfile,
  } = useStore();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(buildForm(user));

  useEffect(() => {
    if (isAuthenticated) fetchProfile();
  }, [isAuthenticated, fetchProfile]);

  useEffect(() => {
    if (!editing) setForm(buildForm(user));
  }, [user, editing]);

  const handleChange = (event) => {
    setForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  };

  const handleSave = async () => {
    if (!editing) {
      setEditing(true);
      return;
    }

    try {
      await updateProfile({ name: form.name.trim() });
      toast.success("Profile updated");
      setEditing(false);
    } catch (error) {
      toast.error(error.message || "Failed to update profile");
    }
  };

  if (profile_loading && !user) {
    return <Loader compact label="Loading profile" />;
  }

  return (
    <div>
      <div className="mb-5 flex flex-col gap-3 border-b border-gray-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">My Account</h1>
          <p className="mt-1 text-sm text-gray-500">
            View and update your account details.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={profile_loading}
          className="inline-flex items-center justify-center gap-2 rounded-md border border-primary-600 bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
        >
          {editing ? (
            <>
              <CheckOutlinedIcon style={{ fontSize: 16 }} />
              {profile_loading ? "Saving..." : "Save"}
            </>
          ) : (
            <>
              <EditOutlinedIcon style={{ fontSize: 16 }} />
              Edit
            </>
          )}
        </button>
      </div>

      {profile_error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {profile_error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="Full Name"
          value={form.name}
          editing={editing}
          name="name"
          onChange={handleChange}
        />
        <Field
          label="Mobile Number"
          value={form.mobile}
          editing={editing}
          name="mobile"
          onChange={handleChange}
          disabled
        />
        <div className="sm:col-span-2">
          <Field
            label="Email Address"
            value={form.email}
            editing={editing}
            name="email"
            onChange={handleChange}
            disabled
          />
        </div>
      </div>
    </div>
  );
};

export default ProfileSection;
